import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDeal } from "../lib/store";
import { instantlyApi, mailApi, bdApi, dealsApi } from "../lib/api";
import { SequenceCampaignModal } from "../components/SequencePicker";
import { ComposeEmailModal } from "../components/ComposeEmailModal";
import { MeetingInviteModal } from "../components/MeetingInviteModal";
import { toast } from "sonner";
import {
  Inbox, RefreshCcw, Sparkles, Send, ThumbsUp, ThumbsDown, CalendarCheck, PartyPopper,
  Palmtree, UserX, Users, ChevronDown, ArrowRight, Mail, UserPlus, Radio, ListOrdered, Unplug, Link2, Briefcase, Target, ReplyAll, FolderInput, CalendarPlus,
} from "lucide-react";

const STATUS = [
  ["Lead", Users],
  ["Interested", ThumbsUp],
  ["Meeting booked", CalendarCheck],
  ["Won", PartyPopper],
  ["Out of office", Palmtree],
  ["Wrong person", UserX],
  ["Not interested", ThumbsDown],
];
const TONE = { Interested: "go", "Meeting booked": "go", Won: "go", "Not interested": "pass", "Wrong person": "pass" };
const when = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso), diff = (Date.now() - d.getTime()) / 6e4;
  if (diff < 60) return `${Math.max(1, Math.round(diff))}m ago`;
  if (diff < 1440) return `${Math.round(diff / 60)}h ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};
const ping = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(), g = ctx.createGain();
    o.type = "sine"; o.frequency.value = 880; g.gain.value = 0.04;
    o.connect(g); g.connect(ctx.destination); o.start();
    o.frequency.setValueAtTime(1320, ctx.currentTime + 0.09); o.stop(ctx.currentTime + 0.2);
  } catch { /* no audio */ }
};

const Group = ({ title, children, testid }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="ub-group">
      <button className="ub-group-h" onClick={() => setOpen(!open)} data-testid={testid}>
        {title}<ChevronDown size={13} className={open ? "" : "rot"} />
      </button>
      {open && <div className="ub-group-b">{children}</div>}
    </div>
  );
};

export default function OutreachInbox() {
  const { M, dealName } = useDeal();
  const dealKey = M.dealKey || "";
  const [rows, setRows] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [configured, setConfigured] = useState(true);
  const [eaccount, setEaccount] = useState("");
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [campaign, setCampaign] = useState("");
  const [scope, setScope] = useState("all");
  const [mail, setMail] = useState("campaign");
  const [folder, setFolder] = useState("inbox");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [selId, setSelId] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState("");
  const [live, setLive] = useState(false);
  const [hook, setHook] = useState(null);
  const [ms, setMs] = useState(null);
  const [thread, setThread] = useState([]);
  const [seqOpen, setSeqOpen] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composePrefill, setComposePrefill] = useState(null);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [meetingPrefill, setMeetingPrefill] = useState(null);
  const [bdPlatforms, setBdPlatforms] = useState([]);
  const [bdPlatform, setBdPlatform] = useState("");
  const [deals, setDeals] = useState([]);
  const [omDeal, setOmDeal] = useState("");
  const mailRef = useRef(mail);
  mailRef.current = mail;

  const load = useCallback(async (quiet) => {
    if (!quiet) setLoading(true);
    try {
      if (mail === "outlook") {
        const r = await mailApi.messages({ folder, ...(scope === "deal" && dealKey ? { dealKey } : {}), limit: 150, force: !!quiet });
        setMs((m) => ({ ...(m || {}), configured: r.configured, connected: r.connected, connectedAs: r.mailbox || m?.connectedAs, lastSync: r.lastSync }));
        setRows(r.replies || []);
        setEaccount(r.mailbox || "");
      } else {
        const r = await instantlyApi.inbox({ ...(campaign ? { campaign_id: campaign } : {}), ...(scope === "deal" && dealKey ? { dealKey } : {}), mail, limit: 100 });
        setConfigured(r.configured !== false);
        setRows(r.replies || []);
        setEaccount(r.eaccount || "");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the inbox");
    } finally { setLoading(false); }
  }, [campaign, scope, dealKey, mail, folder]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    instantlyApi.campaigns().then((r) => setCampaigns(r.campaigns || [])).catch(() => {});
    instantlyApi.webhookStatus().then(setHook).catch(() => {});
    mailApi.status().then(setMs).catch(() => {});
    bdApi.platforms().then((ps) => { setBdPlatforms(ps || []); if ((ps || []).length) setBdPlatform((cur) => cur || ps[0].id); }).catch(() => {});
    dealsApi.list().then((ds) => {
      const opts = (ds || []).map((d) => ({ key: (d.model && d.model.dealKey) || d.id, name: d.name || "Untitled Deal" })).filter((d) => d.key);
      setDeals(opts);
      if (opts.length) setOmDeal((cur) => cur || (opts.find((o) => o.key === dealKey)?.key) || opts[0].key);
    }).catch(() => {});
    const q = new URLSearchParams(window.location.search);
    if (q.get("ms")) {
      if (q.get("ms") === "connected") { toast.success("Microsoft 365 mailbox connected"); setMail("outlook"); }
      else toast.error(q.get("msg") || "Microsoft sign-in failed");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line

  // Live push: Instantly webhook → SSE. Auto-refresh Outlook every 3 minutes.
  useEffect(() => {
    let es = null;
    let closed = false;
    const onMessage = (ev) => {
      let d = {};
      try { d = JSON.parse(ev.data); } catch { return; }
      if (d.type === "hello") setLive(true);
      if (d.type === "reply" && d.reply) {
        ping();
        toast.success(`New reply from ${d.reply.contactName || d.reply.from}${d.reply.sentiment ? ` · ${d.reply.sentiment}` : ""}`, { description: d.reply.summary || d.reply.subject });
        if (mailRef.current !== "outlook") setRows((rs) => [d.reply, ...rs.filter((x) => x.id !== d.reply.id)]);
      }
      if (d.type === "status") load(true);
    };
    instantlyApi.eventsUrl().then((url) => {
      if (closed) return;
      es = new EventSource(url);
      es.onmessage = onMessage;
      es.onerror = () => setLive(false);
    }).catch(() => setLive(false));
    const t = setInterval(() => { if (mailRef.current === "outlook") load(true); }, 180000);
    return () => { closed = true; if (es) es.close(); clearInterval(t); };
  }, []); // eslint-disable-line

  const filtered = useMemo(() => rows.filter((r) => {
    if (status && r.sentiment !== status) return false;
    if (unreadOnly && r.read) return false;
    return true;
  }), [rows, status, unreadOnly]);
  const sel = filtered.find((r) => r.id === selId) || filtered[0] || null;
  const counts = useMemo(() => {
    const c = {};
    rows.forEach((r) => { if (r.sentiment) c[r.sentiment] = (c[r.sentiment] || 0) + 1; });
    return c;
  }, [rows]);
  const unread = rows.filter((r) => !r.read).length;
  const isOutlook = sel?.source === "outlook";

  const replyAll = () => {
    if (!sel) return;
    const own = (ms?.connectedAs || ms?.mailbox || eaccount || "").toLowerCase();
    const uniq = (arr) => [...new Set((arr || []).map((x) => (x || "").toLowerCase()).filter(Boolean))];
    let toAddr = "";
    let ccArr = [];
    if (sel.folder === "sent") {
      const recips = uniq(sel.to);
      toAddr = recips[0] || "";
      ccArr = uniq([...recips.slice(1), ...(sel.cc || [])]).filter((e) => e !== toAddr && e !== own);
    } else {
      toAddr = (sel.from || "").toLowerCase();
      ccArr = uniq([...(sel.to || []), ...(sel.cc || [])]).filter((e) => e !== toAddr && e !== own);
    }
    if (!toAddr) return toast.error("No recipient found on that email");
    const subj = /^re:/i.test(sel.subject || "") ? sel.subject : `Re: ${sel.subject || ""}`.trim();
    setComposePrefill({ to: toAddr, cc: ccArr.join(", "), subject: subj });
    setComposeOpen(true);
  };

  const inviteMeeting = () => {
    if (!sel) { setMeetingPrefill(null); setMeetingOpen(true); return; }
    const own = (ms?.connectedAs || ms?.mailbox || eaccount || "").toLowerCase();
    const toAddr = sel.folder === "sent" ? ((sel.to || [])[0] || "") : (sel.from || "");
    setMeetingPrefill({ to: (toAddr || "").toLowerCase(), name: sel.contactName || sel.fromName || "", cc: "" });
    if (!toAddr && own) { /* nothing */ }
    setMeetingOpen(true);
  };

  useEffect(() => { setDraft(sel?.aiDraft || ""); }, [sel?.id]); // eslint-disable-line
  useEffect(() => {
    if (!sel || sel.source !== "outlook" || !sel.threadId) { setThread([]); return; }
    mailApi.thread(sel.threadId).then((r) => setThread((r.messages || []).filter((m) => m.id !== sel.id))).catch(() => setThread([]));
  }, [sel?.id]); // eslint-disable-line

  const open = async (r) => {
    setSelId(r.id);
    if (!r.read) {
      setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, read: true } : x)));
      (r.source === "outlook" ? mailApi.markRead(r.id) : instantlyApi.markRead(r.threadId || r.id, r.id)).catch(() => {});
    }
  };

  const classify = async () => {
    const targets = filtered.filter((r) => !r.sentiment).slice(0, 15);
    if (!targets.length) return toast.info("Every reply in view is already labelled");
    setBusy("ai");
    try {
      const { results } = await instantlyApi.classify({ replies: targets, dealName: dealName || M.dealName, advance: true });
      setRows((rs) => rs.map((x) => {
        const hit = (results || []).find((v) => v.id === x.id);
        return hit ? { ...x, ...hit } : x;
      }));
      const moved = (results || []).filter((r) => r.movedTo).length;
      toast.success(`${(results || []).length} replies labelled${moved ? ` · ${moved} CRM stage(s) advanced` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI labelling failed");
    } finally { setBusy(""); }
  };

  const setStatus_ = async (status) => {
    if (!sel) return;
    setBusy("st");
    try {
      const r = await instantlyApi.setStatus({ email_id: sel.id, status, from_email: sel.from, advance: true });
      setRows((rs) => rs.map((x) => (x.id === sel.id ? { ...x, sentiment: status, link: r.movedTo ? { ...x.link, stage: r.movedTo } : x.link } : x)));
      toast.success(`Marked ${status}${r.movedTo ? ` · CRM moved to ${r.movedTo}` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the status");
    } finally { setBusy(""); }
  };

  const saveLead = async (kind, extra = {}) => {
    if (!sel) return;
    setBusy("save");
    try {
      const dealForKind = kind === "offmarket"
        ? { dealKey: extra.dealKey || omDeal, dealName: (deals.find((d) => d.key === (extra.dealKey || omDeal))?.name) || dealName || M.dealName }
        : { dealKey, dealName: dealName || M.dealName };
      const r = await instantlyApi.saveLead({
        email: sel.from, name: sel.contactName || sel.fromName || "", company: sel.company,
        kind, ...dealForKind, platformId: extra.platformId || "",
        notes: `From ${sel.campaignName || (isOutlook ? "Outlook inbox" : "inbox")}: ${(sel.body || "").slice(0, 200)}`,
      });
      const stageByKind = { debt: "Contacted", offmarket: "Contacted", bizdev: "Contacted" };
      const label = { debt: "lender", offmarket: "off-market", bizdev: "BizDev" }[kind] || "LP";
      setRows((rs) => rs.map((x) => (x.id === sel.id ? { ...x, link: { kind, id: r.id, name: r.name || sel.contactName || sel.from, stage: stageByKind[kind] || "Emailed" } } : x)));
      toast.success(r.created ? `${r.name} saved to the ${label} pipeline` : "Already in the pipeline");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the contact");
    } finally { setBusy(""); }
  };

  const send = async () => {
    if (!sel || !draft.trim()) return toast.error("Write a reply first");
    setBusy("send");
    try {
      if (isOutlook) await mailApi.reply({ message_id: sel.id, to_email: sel.from, subject: sel.subject, text: draft.trim() });
      else await instantlyApi.reply({ email_id: sel.id, eaccount: sel.eaccount || eaccount, to_email: sel.from, subject: sel.subject, text: draft.trim() });
      setRows((rs) => rs.map((x) => (x.id === sel.id ? { ...x, repliedAt: new Date().toISOString(), read: true } : x)));
      setDraft("");
      toast.success(`Reply sent to ${sel.from}${isOutlook ? " from Outlook" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the reply");
    } finally { setBusy(""); }
  };

  const advance = async (stage) => {
    if (!sel?.link) return;
    setBusy("adv");
    try {
      await instantlyApi.advance({ email: sel.from, kind: sel.link.kind, stage });
      setRows((rs) => rs.map((x) => (x.id === sel.id ? { ...x, link: { ...x.link, stage } } : x)));
      toast.success(`${sel.link.name || sel.from} moved to ${stage}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update the CRM");
    } finally { setBusy(""); }
  };

  const connectHook = async () => {
    setBusy("hook");
    try {
      const r = await instantlyApi.webhookConnect(window.location.origin);
      setHook((h) => ({ ...(h || {}), ...r }));
      toast.success(r.created ? "Instant reply alerts are on — Instantly will push every reply here" : "Webhook already connected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not register the webhook");
    } finally { setBusy(""); }
  };

  const connectMs = async () => {
    setBusy("ms");
    try {
      const r = await mailApi.connect(window.location.origin);
      window.location.href = r.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Microsoft 365 connection is not configured");
      setBusy("");
    }
  };

  const disconnectMs = async () => {
    try { await mailApi.disconnect(); setMs((m) => ({ ...(m || {}), connected: false, connectedAs: "" })); setMail("campaign"); toast.success("Mailbox disconnected"); }
    catch { toast.error("Could not disconnect"); }
  };

  return (
    <div className="page ub" data-testid="page-inbox">
      <div className="page-head ub-head">
        <div>
          <div className="page-title">Outreach Inbox</div>
          <div className="page-sub">Every reply from your Instantly campaigns and your Microsoft 365 inbox — labelled, linked to the CRM and answerable here.</div>
        </div>
        <div className="ub-head-acts">
          <span className={`ub-live ${live ? "on" : ""}`} title={live ? "Connected to live updates" : "Live updates offline"} data-testid="ub-live"><Radio size={12} />{live ? "Live" : "Offline"}</span>
          <span className="ub-count" data-testid="ub-unread">{unread} unread · {rows.length} {mail === "outlook" ? "emails" : "replies"}</span>
          <button className="btn btn-primary" onClick={() => { setComposePrefill(null); setComposeOpen(true); }} data-testid="ub-compose"><Mail size={13} />New email</button>
          <button className="btn" onClick={() => { setMeetingPrefill(null); setMeetingOpen(true); }} data-testid="ub-meeting"><CalendarPlus size={13} />Meeting invite</button>
          <button className="btn" onClick={() => setSeqOpen(true)} data-testid="ub-new-sequence"><ListOrdered size={13} />New sequence</button>
          <button className="btn" onClick={classify} disabled={busy === "ai"} data-testid="ub-classify"><Sparkles size={13} />{busy === "ai" ? "Reading…" : "AI label + draft"}</button>
          <button className="btn" onClick={() => load(true)} data-testid="ub-refresh"><RefreshCcw size={13} />Refresh</button>
        </div>
      </div>

      {!configured && mail !== "outlook" && <div className="cm-empty-block" data-testid="ub-not-configured">Instantly is not connected yet — add INSTANTLY_API_KEY to the backend to pull replies.</div>}
      {hook && hook.configured && !hook.connected && mail !== "outlook" && (
        <div className="ub-banner" data-testid="ub-hook-banner">
          <span><b>Instant reply alerts are off.</b> Register the Instantly webhook once and new replies appear here the second they land — pre-labelled by AI, CRM stage advanced.</span>
          <button className="btn btn-primary btn-sm" onClick={connectHook} disabled={busy === "hook"} data-testid="ub-hook-connect"><Link2 size={12} />{busy === "hook" ? "Connecting…" : "Turn on instant alerts"}</button>
        </div>
      )}

      <div className="ub-grid">
        <aside className="ub-rail" data-testid="ub-rail">
          <div className="ub-rail-h">Status</div>
          <button className={`ub-st ${!status ? "on" : ""}`} onClick={() => setStatus("")} data-testid="ub-status-all">
            <Inbox size={14} />{mail === "outlook" ? "All emails" : "All replies"}<span>{rows.length}</span>
          </button>
          {STATUS.map(([s, Icon]) => (
            <button key={s} className={`ub-st ${status === s ? "on" : ""} ${TONE[s] || ""}`} onClick={() => setStatus(status === s ? "" : s)} data-testid={`ub-status-${s.replace(/\s+/g, "-").toLowerCase()}`}>
              <Icon size={14} />{s}<span>{counts[s] || 0}</span>
            </button>
          ))}
          <Group title="Mail source" testid="ub-group-mail">
            <button className={`ub-sub ${mail === "campaign" ? "on" : ""}`} onClick={() => setMail("campaign")} data-testid="ub-mail-campaign">Campaign replies</button>
            <button className={`ub-sub ${mail === "all" ? "on" : ""}`} onClick={() => setMail("all")} data-testid="ub-mail-all">All Instantly mail</button>
            <button className={`ub-sub ${mail === "outlook" ? "on" : ""}`} onClick={() => setMail("outlook")} data-testid="ub-mail-outlook">Outlook · {ms?.connectedAs || ms?.mailbox || "Microsoft 365"}</button>
          </Group>
          {mail === "outlook" && (
            <Group title="Outlook folder" testid="ub-group-folder">
              <button className={`ub-sub ${folder === "inbox" ? "on" : ""}`} onClick={() => setFolder("inbox")} data-testid="ub-folder-inbox"><Inbox size={12} style={{ marginRight: 5 }} />Inbox</button>
              <button className={`ub-sub ${folder === "sent" ? "on" : ""}`} onClick={() => setFolder("sent")} data-testid="ub-folder-sent"><Send size={12} style={{ marginRight: 5 }} />Sent</button>
              <button className={`ub-sub ${folder === "all" ? "on" : ""}`} onClick={() => setFolder("all")} data-testid="ub-folder-all"><FolderInput size={12} style={{ marginRight: 5 }} />Inbox + Sent</button>
            </Group>
          )}
          {mail !== "outlook" && (
            <Group title="All Campaigns" testid="ub-group-campaigns">
              <button className={`ub-sub ${!campaign ? "on" : ""}`} onClick={() => setCampaign("")} data-testid="ub-camp-all">Every campaign</button>
              {campaigns.map((c) => (
                <button key={c.id} className={`ub-sub ${campaign === c.id ? "on" : ""}`} onClick={() => setCampaign(c.id)} data-testid={`ub-camp-${c.id}`}>{c.name}</button>
              ))}
              {!campaigns.length && <div className="ub-none">No campaigns yet</div>}
            </Group>
          )}
          <Group title="Deal scope" testid="ub-group-scope">
            <button className={`ub-sub ${scope === "all" ? "on" : ""}`} onClick={() => setScope("all")} data-testid="ub-scope-all">All deals</button>
            <button className={`ub-sub ${scope === "deal" ? "on" : ""}`} onClick={() => setScope("deal")} data-testid="ub-scope-deal">{dealName || M.dealName}</button>
          </Group>
          <Group title="More" testid="ub-group-more">
            <label className="ub-chk"><input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} data-testid="ub-unread-only" />Unread only</label>
            <div className="ub-none">Sending as {eaccount || "—"}</div>
            {hook?.connected && <div className="ub-none" data-testid="ub-hook-on">Webhook on · instant alerts</div>}
            {ms?.connected && <button className="ub-sub" onClick={disconnectMs} data-testid="ub-ms-disconnect"><Unplug size={11} style={{ marginRight: 5 }} />Disconnect Outlook</button>}
          </Group>
        </aside>

        <div className="ub-list" data-testid="ub-list">
          {mail === "outlook" && ms && !ms.connected && (
            <div className="ub-empty" data-testid="ub-ms-connect">
              <Mail size={22} />
              <b>Connect {ms.mailbox || "your Microsoft 365 mailbox"}</b>
              <span>{ms.configured ? "One-click sign-in pulls your Inbox from the last 30 days, refreshed every few minutes." : "Add MS_TENANT_ID, MS_CLIENT_ID, MS_CLIENT_SECRET and MS_REDIRECT_URI to the backend, then connect."}</span>
              <button className="btn btn-primary" onClick={connectMs} disabled={busy === "ms" || !ms.configured} data-testid="ub-ms-connect-btn">{busy === "ms" ? "Redirecting…" : "Connect Microsoft 365"}</button>
            </div>
          )}
          {loading && <div className="ub-none pad">Loading {mail === "outlook" ? "mailbox" : "replies"}…</div>}
          {!loading && !filtered.length && !(mail === "outlook" && ms && !ms.connected) && (
            <div className="ub-empty" data-testid="ub-empty">
              <Mail size={22} />
              <b>{mail === "outlook" ? "No emails in this folder" : "No replies here yet"}</b>
              <span>{mail === "outlook" ? "Nothing in the last 30 days matches these filters." : "Add contacts to an Instantly campaign from any CRM — answers land in this inbox."}</span>
            </div>
          )}
          {filtered.map((r) => (
            <button key={r.id} className={`ub-item ${sel?.id === r.id ? "on" : ""} ${r.read ? "" : "unread"}`} onClick={() => open(r)} data-testid={`ub-item-${r.id}`}>
              <div className="ub-item-top">
                <b>{r.folder === "sent" ? `To: ${(r.to || [])[0] || "—"}` : (r.contactName || r.from || "Unknown")}</b>
                <span className="ub-when">{when(r.at)}</span>
              </div>
              <div className="ub-item-sub">{r.subject}</div>
              <div className="ub-item-pre">{(r.body || "").slice(0, 110)}</div>
              <div className="ub-item-tags">
                {r.sentiment && <span className={`tag ${TONE[r.sentiment] === "go" ? "tag-go" : TONE[r.sentiment] === "pass" ? "tag-pass" : ""}`}>{r.sentiment}</span>}
                {r.interest && <span className="tag">{r.interest}</span>}
                {r.campaignName && <span className="ub-camp">{r.campaignName}</span>}
                {r.source === "outlook" && <span className="ub-camp">{r.folder === "sent" ? "Sent" : "Outlook"}</span>}
              </div>
            </button>
          ))}
        </div>

        <div className="ub-thread" data-testid="ub-thread">
          {!sel && <div className="ub-none pad">Pick a {mail === "outlook" ? "message" : "reply"} to read it.</div>}
          {sel && (
            <>
              <div className="ub-th-head">
                <div>
                  <b>{sel.contactName || sel.fromName || sel.from}</b>
                  <small>{sel.from}{sel.company ? ` · ${sel.company}` : ""}{sel.dealName ? ` · ${sel.dealName}` : ""}{isOutlook && sel.to?.length ? ` · to ${sel.to.join(", ")}` : ""}{isOutlook && sel.cc?.length ? ` · cc ${sel.cc.join(", ")}` : ""}</small>
                </div>
                <div className="ub-th-tags">
                  {isOutlook && <button className="btn btn-sm" onClick={replyAll} data-testid="ub-reply-all"><ReplyAll size={12} />Reply all</button>}
                  <button className="btn btn-sm" onClick={inviteMeeting} data-testid="ub-thread-meeting"><CalendarPlus size={12} />Meeting invite</button>
                  {sel.sentiment && <span className={`tag ${TONE[sel.sentiment] === "go" ? "tag-go" : TONE[sel.sentiment] === "pass" ? "tag-pass" : ""}`} data-testid="ub-th-sentiment">{sel.sentiment}</span>}
                  {sel.interest && <span className="tag">{sel.interest}</span>}
                </div>
              </div>
              {sel.summary && <div className="ub-summary" data-testid="ub-summary">{sel.summary}</div>}

              <div className="ub-status-bar" data-testid="ub-status-bar">
                <span className="ub-status-lbl">Mark as</span>
                {STATUS.map(([s, Icon]) => (
                  <button key={s} className={`ub-status-btn ${sel.sentiment === s ? "on" : ""} ${TONE[s] || ""}`} disabled={busy === "st"} onClick={() => setStatus_(s)} data-testid={`ub-set-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                    <Icon size={11} />{s}
                  </button>
                ))}
              </div>

              {thread.length > 0 && (
                <div className="ub-history" data-testid="ub-history">
                  {thread.map((m) => (
                    <div key={m.id} className={`ub-hist ${m.folder === "sent" ? "me" : ""}`}>
                      <div className="ub-hist-h"><b>{m.folder === "sent" ? "You" : (m.fromName || m.from)}</b><span>{when(m.at)}</span></div>
                      <div className="ub-hist-b">{(m.body || "").slice(0, 600)}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="ub-subject">{sel.subject}</div>
              <div className="ub-body" data-testid="ub-body">{sel.body}</div>

              {sel.link && (
                <div className="ub-link" data-testid="ub-link">
                  <span>{sel.link.kind === "debt" ? "Lender" : sel.link.kind === "offmarket" ? "Seller target" : sel.link.kind === "bizdev" ? "BD prospect" : "LP"} in CRM · <b>{sel.link.name}</b>{sel.link.company && ["offmarket", "bizdev"].includes(sel.link.kind) ? ` (${sel.link.company})` : ""} · stage {sel.link.stage || "—"}</span>
                  <span className="ub-link-acts">
                    {(sel.link.kind === "debt" ? ["NDA", "Materials Sent", "Term Sheet", "Passed"] : sel.link.kind === "offmarket" ? ["Interested", "NDA", "LOI", "Passed"] : sel.link.kind === "bizdev" ? ["Meeting", "Proposal", "Won", "Lost"] : ["Intro Call", "Diligence", "Soft Circle", "Passed"]).map((s) => (
                      <button key={s} className="btn btn-sm" disabled={busy === "adv" || sel.link.stage === s} onClick={() => advance(s)} data-testid={`ub-adv-${s.replace(/\s+/g, "-").toLowerCase()}`}>
                        <ArrowRight size={11} />{s}
                      </button>
                    ))}
                  </span>
                </div>
              )}

              {!sel.link && sel.folder !== "sent" && (
                <div className="ub-link ub-save-wrap" data-testid="ub-save-lead">
                  <span>{sel.from} is not in a pipeline yet — save the contact so follow-ups, stages and activity are tracked.</span>
                  <span className="ub-link-acts">
                    <button className="btn btn-sm btn-primary" disabled={busy === "save"} onClick={() => saveLead("capital")} data-testid="ub-save-lp"><UserPlus size={11} />Save as LP</button>
                    <button className="btn btn-sm" disabled={busy === "save"} onClick={() => saveLead("debt")} data-testid="ub-save-lender"><UserPlus size={11} />Save as lender</button>
                  </span>
                  <span className="ub-save-row">
                    <select className="inp inp-sm" value={bdPlatform} onChange={(e) => setBdPlatform(e.target.value)} data-testid="ub-bizdev-platform" title="Choose the BizDev platform">
                      {bdPlatforms.length === 0 && <option value="">No platforms yet</option>}
                      {bdPlatforms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <button className="btn btn-sm" disabled={busy === "save" || !bdPlatform} onClick={() => saveLead("bizdev", { platformId: bdPlatform })} data-testid="ub-save-bizdev"><Briefcase size={11} />Save as BizDev</button>
                  </span>
                  <span className="ub-save-row">
                    <select className="inp inp-sm" value={omDeal} onChange={(e) => setOmDeal(e.target.value)} data-testid="ub-offmarket-platform" title="Choose the platform / deal">
                      {deals.length === 0 && <option value="">No platforms yet</option>}
                      {deals.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
                    </select>
                    <button className="btn btn-sm" disabled={busy === "save" || !omDeal} onClick={() => saveLead("offmarket", { dealKey: omDeal })} data-testid="ub-save-offmarket"><Target size={11} />Save as Off-market</button>
                  </span>
                </div>
              )}

              {sel.folder !== "sent" && (
                <div className="ub-reply">
                  <label>Your reply{sel.repliedAt ? ` · last sent ${when(sel.repliedAt)}` : ""}</label>
                  <textarea className="inp" rows={7} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Write a reply, or hit AI label + draft to get a first pass…" data-testid="ub-draft" />
                  <div className="ub-reply-acts">
                    <span className="cm-empty">Sends from {sel.eaccount || eaccount || "your account"} {isOutlook ? "via Microsoft 365" : "inside the same Instantly thread"}.</span>
                    <button className="btn btn-primary" onClick={send} disabled={busy === "send" || !draft.trim()} data-testid="ub-send"><Send size={13} />{busy === "send" ? "Sending…" : "Send reply"}</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <SequenceCampaignModal open={seqOpen} onClose={() => setSeqOpen(false)} onCreated={(c) => setCampaigns((cs) => [c, ...cs])} />
      <ComposeEmailModal open={composeOpen} onClose={() => setComposeOpen(false)} prefill={composePrefill} outlookConnected={!!(ms?.connected)} mailbox={ms?.connectedAs || ms?.mailbox || eaccount} dealName={dealName || M.dealName} />
      <MeetingInviteModal open={meetingOpen} onClose={() => setMeetingOpen(false)} prefill={meetingPrefill} outlookConnected={!!(ms?.connected)} mailbox={ms?.connectedAs || ms?.mailbox || eaccount} dealName={dealName || M.dealName} />
    </div>
  );
}
